import CortoLoader from "_src/engine/loaders/corto/corto_loader";
import ZStdLoader from "_src/engine/loaders/zstd/zstd_loader";
import PlnLoader from "_src/engine/loaders/pln/pln_loader";
import events from "_src/engine/events";
import workerManager from "_src/engine/worker_manager.js";
import { networkStatus } from "_src/engine/constants";
import Image2D from "_image";
import AbortController from "_abort_controller";
import config from "_src/config";
import containersCache from "_src/engine/packers/containers_cache";
import ObjLoader from "_src/engine/loaders/external/obj/obj_loader";
import StlLoader from "_src/engine/loaders/external/stl/stl_loader";
import PlyLoader from "_src/engine/loaders/external/ply/ply_loader";
import VrmlLoader from "_src/engine/loaders/external/vrml_loader";
import GltfLoader from "_src/engine/loaders/external/gltf_loader";
import UsdLoader from "_src/engine/loaders/external/usd/usd_loader";
import downloader from "_src/engine/loaders/downloader";

let cortoLoader = null;
let zstdLoader = null;
let plnLoader = null;

const abortControllers = [];

const MAX_LOADING_THREADS = 6;

const ABORT_ERROR      = "AbortError";
const ABORT_EVERYTHING = "0";
const ABORT_BY_TYPE    = "1";
const ABORT_BY_PATH    = "2";

const _activeAssetGroups = [];
let _isStoped = false;
let  _isWarmedUp = false;
const _assetGroupQueue = [];

export default {

  JSON          : 0,
  EXTERNAL      : 1,
  IMAGE         : 2,
  GEOMETRY      : 3,

  abort() {

    this.stopLoading();
    this.cleanup();

  },

  abortByType(type) {

    this._stop();

    this._abortAssetGroupByCondition((assetGroup) => {
      return assetGroup.type === type;
    }, ABORT_BY_TYPE);

    this._continue();

  },

  abortByPath(path) {

    this._stop();

    this._abortAssetGroupByCondition((assetGroup) => {
      return assetGroup.path === path;
    }, ABORT_BY_PATH);

    this._continue();

  },

  abortByPathAndId(path, id) {
    for (const assetGroup of _assetGroupQueue){
      if (assetGroup.path !== path) {
        continue;
      }

      for (const asset of assetGroup.assets.slice()) {
        const bufferId = asset.payload && asset.payload.id !== undefined ? asset.payload.id : 0;
        if (bufferId !== id) {
          continue;
        }

        asset.reject(ABORT_BY_PATH);
        assetGroup.assets = assetGroup.assets.filter(a => a !== asset);
      }
    }

    for (const activeAssetGroup of _activeAssetGroups) {
      if (activeAssetGroup.path !== path) {
        continue;
      }
      for (const asset of activeAssetGroup.assets.slice()) {
        const bufferId = asset.payload && asset.payload.id !== undefined ? asset.payload.id : 0;
        if (bufferId !== id) {
          continue;
        }

        if (asset.loadingReject) {
          asset.loadingReject(ABORT_BY_PATH);
        } else {
          asset.reject(ABORT_BY_PATH);
        }
        if (asset.abortController) asset.abortController.abort();

        activeAssetGroup.assets = activeAssetGroup.assets.filter(a => a !== asset);
      }
    }
  },

  cleanup() {

    this._abortAssetGroupByCondition(() => {
      return true;
    }, ABORT_EVERYTHING);

    _assetGroupQueue.length = 0;
    _activeAssetGroups.length = 0;
    _isWarmedUp = false;

  },

  load(path, type, payload = null) {

    const pushPromise = this._push(path, type, payload);
    this._pop();

    return pushPromise;

  },

  _stop() {
    _isStoped = true;
  },

  _continue() {

    _isStoped = false;
    this._pop();

  },

  _push(path, type, payload) {

    const promise = new Promise((resolve, reject) => {

      const resolveHandler = (loadedData) => {
        resolve(loadedData);
      };

      const rejectHandler = (error) => {
        reject(error);
      };

      const asset = {
        resolve       : resolveHandler,
        reject        : rejectHandler,
        loadingReject : null,
        abortController: null,
        payload       : payload,
        tid           : workerManager.generateUniqueID()
      };

      let assetGroup = _assetGroupQueue.find(group => group.path === path);

      if (!assetGroup) {
        assetGroup = _activeAssetGroups.find(group => group.path === path);
      }

      if (!assetGroup) {
        assetGroup = {
          path: path,
          type: type,
          priority: Infinity,
          assets: []
        };
        _assetGroupQueue.push(assetGroup);
      }

      assetGroup.assets.push(asset);

    });

    return promise;

  },

  async _pop() {

    if (_isStoped) {
      return null;
    }

    if (_activeAssetGroups.length >= MAX_LOADING_THREADS) {
      return null;
    }

    events.emit("shift_loading_queue", _assetGroupQueue);

    _assetGroupQueue.sort((a, b) => {
      return a.priority - b.priority;
    });

    const assetGroup = _assetGroupQueue.shift();

    if (assetGroup) {
      _activeAssetGroups.push(assetGroup);

      const path = assetGroup.path;
      const type = assetGroup.type;

      let assetsProcessed = 0;

      while (assetsProcessed < assetGroup.assets.length) {

        const promises = [];

        for (let i = assetsProcessed; i<assetGroup.assets.length; i++) {
          const asset = assetGroup.assets[i];

          const bufferId = asset.payload && asset.payload.id !== undefined ? asset.payload.id : 0;
          const progress = (asset.payload || {}).fileProgress;
          const useWorkers = config.useWorkers ? (asset.payload || {}).useWorkers : false;
          const releaseAfterUse = asset.payload && asset.payload.releaseAfterUse !== undefined ? asset.payload.releaseAfterUse : true;

          const loadingPromise = new Promise((resolve, reject) => {
            asset.loadingReject = reject;
            asset.abortController = new AbortController();
            abortControllers.push(asset.abortController);

            let promise;
            switch (type) {
              case this.JSON:
                promise = this._loadJSON(path, asset.abortController);
                break;
              case this.IMAGE:
                promise = this._loadImage(path, asset.abortController, bufferId, progress, releaseAfterUse);
                break;
              case this.EXTERNAL:
                promise = this._loadExternal(path, asset.abortController, useWorkers, progress);
                break;
              case this.GEOMETRY:
                promise = this._loadGeometry(path, asset.abortController, bufferId, asset.tid);
                break;
              default:
                throw "Unknown loading request type.";
            }

            promise.then((result) => {

              resolve(result);

              //NOTE: we assume that at this moment loading system is fully initialized
              if (!_isWarmedUp && type === this.GEOMETRY) {
                events.emit("loader_has_been_warmed_up");
                _isWarmedUp = true;
              }

            }).catch((error) => {

              const errorCode = error.name === ABORT_ERROR ? ABORT_ERROR : error;
              reject(errorCode);

            }).finally(() => {
              const ind = abortControllers.indexOf(asset.abortController);
              if (ind !== -1) {
                abortControllers.splice(ind, 1);
              }
            });

          }).then((result) => {
            asset.resolve(result);
          }).catch((errorCode) => {
            asset.reject(errorCode);
          });

          promises.push(loadingPromise);
        }

        assetsProcessed = assetGroup.assets.length;

        await Promise.allSettled(promises);
      }

      _activeAssetGroups.splice(_activeAssetGroups.indexOf(assetGroup), 1);

      setTimeout(() => {this._pop()}, 0);
    }

  },

  _loadJSON(path, controller) {
    return downloader.download(path, controller, downloader.JSON);
  },

  _loadImage(path, controller, id, progressCallback = null, releaseAfterUse = true) {
    const image = new Image2D();
    return image.loadSliced(path, id, controller, progressCallback, releaseAfterUse);
  },

  _loadExternal: (function () {
    let objLoader, stlLoader, plyLoader, vrmlLoader, gltfLoader, glbLoader, usdLoader;
    return function(path, controller, useWorkers = false, progress = null) {
      const re = /(?:\.([^.]+))?$/;
      const ext = re.exec(path)[1].toLowerCase();
      let loader;
      switch (ext) {
        case "obj": {
          if (!objLoader) objLoader = new ObjLoader();
          loader = objLoader;
          break;
        }
        case "stl": {
          if (!stlLoader) stlLoader = new StlLoader();
          loader = stlLoader;
          break;
        }
        case "ply": {
          if (!plyLoader) plyLoader = new PlyLoader();
          loader = plyLoader;
          break;
        }
        case "wrl": {
          if (!vrmlLoader) vrmlLoader = new VrmlLoader();
          loader = vrmlLoader;
          break;
        }
        case "gltf": {
          if (!gltfLoader) gltfLoader = new GltfLoader(false);
          loader = gltfLoader;
          break;
        }
        case "glb": {
          if (!glbLoader) glbLoader = new GltfLoader(true);
          loader = glbLoader;
          break;
        }
        case "usdz":
        case "usda":
        case "usdc":
        case "usd": {
          if (!usdLoader) usdLoader = new UsdLoader();
          loader = usdLoader;
          break;
        }

        default: {
          throw new Error("Unknown file extension for external loader: " + path);
        }
      }

      return loader.load(path, controller, useWorkers, progress);
    }
  })(),

  async _loadGeometry(path, controller, id, tid) {
    const buffer = await containersCache.getBuffer(path, id, controller);
    const loader = determineLoader(buffer);

    let geometry = null;
    if (config.useWorkers && loader.loadWorker) {
      geometry = await loader.loadWorker(buffer, tid);
    } else {
      geometry = await loader.loadMain(buffer);
    }

    return geometry;
  },

  _abortAssetGroupByCondition(condition, errorCode) {

    let j = 0;
    while(j !== _assetGroupQueue.length) {

      const assetGroup = _assetGroupQueue[j];

      if (condition(assetGroup)) {
        _assetGroupQueue.splice(j, 1);

        for (const asset of assetGroup.assets) {
          asset.reject(errorCode);
        }

      } else {
        ++j;
      }

    }

    for (const activeAssetGroup of _activeAssetGroups) {

      if (condition(activeAssetGroup)) {

        for (const asset of activeAssetGroup.assets) {
          if (asset.loadingReject) {
            asset.loadingReject(errorCode);
          } else {
            asset.reject(errorCode);
          }
          if (asset.abortController) asset.abortController.abort();
        }
        activeAssetGroup.assets.length = 0;

      }

    }

  },

  stopLoading() {

    if (cortoLoader) {
      cortoLoader.stopLoading();
    }

    if (zstdLoader) {
      zstdLoader.stopLoading();
    }

    containersCache.cleanup();

    for (let abortController of abortControllers) {
      abortController.abort();
    }
    abortControllers.length = 0;

  },

  isNetworkError(code) {
    return code === networkStatus.ERROR;
  },

  isLegalError(code) {
    return code === ABORT_EVERYTHING || code === ABORT_BY_PATH ||
        code === ABORT_BY_TYPE || code === ABORT_ERROR || code === networkStatus.ERROR;
  },

  isDecimationError(code) {
    return code === ABORT_BY_PATH || code === ABORT_BY_TYPE ||
        code === ABORT_ERROR;
  },

  isTotalAborted(code) {
    return code === ABORT_EVERYTHING;
  }

};

function determineLoader(data) {
  const compression = containersCache.getBufferType(data);
  switch (compression) {
    case "zstd":
      if (!zstdLoader) {
        zstdLoader = new ZStdLoader(false);
      }
      return zstdLoader;
    case "crt ":
      if (!cortoLoader) {
        cortoLoader = new CortoLoader(false);
      }
      return cortoLoader;
    case "pln ":
      if (!plnLoader) {
        plnLoader = new PlnLoader();
      }
      return plnLoader;
    default:
      return null;
  }
}
